import React, { useEffect, useState } from "react";
import { apiAdmin, BACKEND_URL } from "../../lib/api";
import { Save, Upload, ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

const Settings = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);

  useEffect(() => {
    apiAdmin.get("/settings").then((r) => setForm(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiAdmin.put("/settings", form);
      toast.success("Configuración guardada");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const onHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiAdmin.post("/upload", fd);
      const url = `${BACKEND_URL}${res.data.url}`;
      setForm((f) => ({ ...f, hero_image_url: url }));
      toast.success("Imagen subida. Recuerda GUARDAR para aplicar.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir imagen");
    } finally { setUploadingHero(false); }
  };

  if (!form) return <p className="text-gray-400">Cargando...</p>;

  return (
    <div data-testid="admin-settings" className="max-w-3xl">
      <div className="mb-10">
        <span className="text-xs uppercase tracking-[0.25em] font-bold text-yolk">─ Configuración ─</span>
        <h1 className="mt-2 font-heading text-4xl font-bold text-brand-ink">Ajustes generales</h1>
        <p className="text-gray-600 mt-2">Imagen del hero, descuento global, banner y datos de pago.</p>
      </div>

      <div className="space-y-6">
        {/* HERO IMAGE */}
        <div className="bg-white border-2 border-black p-6 md:p-8 space-y-4">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-mint-deep" />
            <h2 className="font-heading text-xl font-bold text-brand-ink">Imagen del inicio (Hero)</h2>
          </div>
          <p className="text-sm text-gray-600">Sube la foto que aparece al inicio de la página. Si no subes ninguna, se usará la imagen por defecto.</p>

          {form.hero_image_url && (
            <div className="relative inline-block">
              <img src={form.hero_image_url} alt="Hero preview" className="h-48 w-auto object-cover border-2 border-black" />
              <button
                type="button"
                onClick={() => setForm({ ...form, hero_image_url: "" })}
                className="absolute -top-2 -right-2 h-8 w-8 bg-red-500 text-white border-2 border-black flex items-center justify-center rounded-full"
                title="Quitar imagen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div>
            <input
              type="text"
              value={form.hero_image_url || ""}
              onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
              placeholder="URL de imagen o sube un archivo"
              data-testid="settings-hero-url"
              className="w-full border-2 border-black px-4 py-3 text-sm mb-3"
            />
            <label className="btn-chunky inline-flex items-center gap-2 bg-yolk text-black px-4 py-2 cursor-pointer text-sm">
              <Upload className="h-4 w-4" />
              {uploadingHero ? "Subiendo..." : "Subir imagen del hero"}
              <input type="file" accept="image/*" onChange={onHeroUpload} className="hidden" data-testid="settings-hero-upload" />
            </label>
          </div>
        </div>

        {/* GLOBAL DISCOUNT */}
        <div className="bg-white border-2 border-black p-6 md:p-8 space-y-4">
          <h2 className="font-heading text-xl font-bold text-brand-ink">Descuento global de la tienda</h2>
          <p className="text-sm text-gray-600">
            Cuando lo actives, en el carrito se aplicará este % de descuento sobre el subtotal. Es <strong>acumulable</strong> con un cupón si el cliente ingresa uno (los % se suman).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">% Descuento global</label>
              <input
                type="number"
                min="0"
                max="100"
                value={form.global_discount_percent ?? 0}
                onChange={(e) => setForm({ ...form, global_discount_percent: Number(e.target.value) })}
                data-testid="settings-global-discount-pct"
                className="w-full border-2 border-black px-4 py-3 text-sm"
              />
            </div>
            <label className="flex items-center gap-3 pb-3">
              <input
                type="checkbox"
                checked={!!form.global_discount_active}
                onChange={(e) => setForm({ ...form, global_discount_active: e.target.checked })}
                data-testid="settings-global-discount-active"
                className="h-5 w-5"
              />
              <span className="text-sm font-bold">Descuento global ACTIVO</span>
            </label>
          </div>
        </div>

        {/* PROMO BANNER */}
        <div className="bg-white border-2 border-black p-6 md:p-8 space-y-5">
          <h2 className="font-heading text-xl font-bold text-brand-ink">Banner promocional</h2>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Texto del banner</label>
            <textarea value={form.promo_banner} onChange={(e) => setForm({ ...form, promo_banner: e.target.value })} rows={2} data-testid="settings-promo-banner" className="w-full border-2 border-black px-4 py-3 text-sm focus:outline-none focus:border-mint-deep resize-none" />
          </div>
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={form.promo_active} onChange={(e) => setForm({ ...form, promo_active: e.target.checked })} data-testid="settings-promo-active" className="h-4 w-4" />
            <span className="text-sm">Banner promocional activo</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Umbral regalo gratis (CLP)</label>
              <input type="number" value={form.gift_threshold} onChange={(e) => setForm({ ...form, gift_threshold: Number(e.target.value) })} data-testid="settings-gift-threshold" className="w-full border-2 border-black px-4 py-3 text-sm" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Umbral envío gratis (CLP)</label>
              <input type="number" value={form.delivery_threshold} onChange={(e) => setForm({ ...form, delivery_threshold: Number(e.target.value) })} data-testid="settings-delivery-threshold" className="w-full border-2 border-black px-4 py-3 text-sm" />
            </div>
          </div>
        </div>

        {/* PAYMENT */}
        <div className="bg-white border-2 border-black p-6 md:p-8 space-y-5">
          <h2 className="font-heading text-xl font-bold text-brand-ink">Datos de pago</h2>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Link de pago (Mercado Pago, Webpay, etc.)</label>
            <input type="text" value={form.payment_link} onChange={(e) => setForm({ ...form, payment_link: e.target.value })} data-testid="settings-payment-link" placeholder="https://mpago.la/..." className="w-full border-2 border-black px-4 py-3 text-sm" />
            <p className="text-xs text-gray-500 mt-1">Se usa cuando el cliente elige pago con tarjeta.</p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Datos bancarios para transferencia (texto libre)</label>
            <textarea value={form.bank_info} onChange={(e) => setForm({ ...form, bank_info: e.target.value })} rows={5} data-testid="settings-bank-info" placeholder="Banco / Tipo de cuenta / N° cuenta / RUT / Nombre / Email" className="w-full border-2 border-black px-4 py-3 text-sm focus:outline-none focus:border-mint-deep resize-none" />
            <p className="text-xs text-gray-500 mt-1">Estos datos se muestran al cliente cuando elige pagar por transferencia.</p>
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="settings-save-btn" className="btn-chunky inline-flex items-center gap-2 bg-mint-deep text-white px-6 py-3 text-sm">
          <Save className="h-4 w-4" /> {saving ? "Guardando..." : "GUARDAR CAMBIOS"}
        </button>
      </div>
    </div>
  );
};

export default Settings;
